import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/layout/ProtectedRoute';
import LandingPage from './components/auth/LandingPage';
import Dashboard from './pages/Dashboard';
import InvoicePage from './pages/InvoicePage';
import InvoicePreview from './components/invoices/InvoicePreview';
import CustomersPage from './pages/CustomersPage';
import ProductsPage from './pages/ProductsPage';
import TransactionsPage from './pages/TransactionsPage';
import Wallet from './pages/Wallet';
import SettingsPage from './pages/SettingsPage';
import PricingPage from './pages/PricingPage';
import SharedProductPage from './pages/ShareProductPage';
import StorePage from './pages/StoreFrontPage';
import './styles/mobile-nav.css';
import EditCustomer from './components/customers/EditCustomer';
import APIDocumentation from './pages/APIDocumentation';
import TermsOfService from './pages/TermsOfService';
import AllInvoicesPage from './pages/AllInvoices';
import api from "./utils/api"
import OrderTrackingPage from './pages/OrderTrackingPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import MeoAIPage from './pages/MeoAIPage';
import InstallPrompt from './components/pwa/InstallPrompt';
import MainOrdersPage from './pages/MainOrdersPage';
import OrderSuccessPage from './pages/OrdersSuccessPage';
import OrderCallbackPage from './pages/OrderCallbackPage';
import Chats from './pages/ChatFlow';
import RefundRequestPage from './pages/RequestRefundPage';
import RefundStatusPage from './pages/RefundStatusPage';
import CompleteSignupPage from './pages/auth/CompleteSignupPage';
import SelectMarketPage from './pages/SelectMarketPage';
import UploadProductPage from './pages/UploadProductPage';
import MagicLinkPage from './pages/MagicLinkPage';
import EditProduct from './components/products/EditProduct';
import ScanProduct from './components/products/ScanProduct';
import { ThemeProvider } from './components/context/themeContext';
import { MeoProvider } from './contexts/MeoContext';
import { AddressBookProvider } from './contexts/AddressBookContext';
import AdminLogin from './components/admin/AdminLogin';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminForgotPassword from './components/admin/AdminForgotPassword';
import AsksDataComponent from './components/AskDataComponent';
import CatalogInit from './pages/CatalogInit';
import BusinessSsoGate from './components/auth/BusinessSsoGate';
import MapsPage from "./pages/MapsPage";

// Custom hook to detect subdomain
function useSubdomain() {
  const [subdomain, setSubdomain] = useState(null);

  useEffect(() => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    
    // Handle localhost development
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      // For testing: username.localhost:3000
      if (parts.length > 1 && parts[0] !== 'localhost' && parts[0] !== 'www') {
        setSubdomain(parts[0]);
      } else {
        setSubdomain(null);
      }
      return;
    }

    // Production: handle web.paymeo.co and *.web.paymeo.co
    // Check if we're on a subdomain of web.paymeo.co (like smith.web.paymeo.co)
    if (parts.length >= 4 && parts[1] === 'web') {
      // Extract username from smith.web.paymeo.co
      setSubdomain(parts[0]);
    } 
    // Handle paymeo.co or web.paymeo.co
    else {
      setSubdomain(null);
    }
  }, []);

  return subdomain;
}

function AppRoutes() {
  const { currentUser, loading } = useAuth();
  const subdomain = useSubdomain();
const navigate = useNavigate()
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-gradient-to-b from-blue-600 to-blue-500">
        <img
          src="https://cdn.paymeo.co/uploads/brand/1788303485685_b61c6298a899909071.png"
          alt="Paymeo Logo"
          className="w-20 h-20 animate-heartbeat select-none"
        />
      </div> 
    );
  }

  // If we're on a subdomain (like smith.web.paymeo.co), show ONLY store-related routes
  if (subdomain) {
    return (
      <Routes>
        {/* Store front route for subdomains - PUBLICLY ACCESSIBLE */}
        <Route path="/" element={<StorePage username={subdomain} />} />
        <Route path="/product/:productId" element={<SharedProductPage />} />
        <Route path="/track/:orderId?" element={<OrderTrackingPage />} />
        <Route path="/confirm-delivery/:orderId?" element={<OrderConfirmationPage />} />
        <Route path="/orders-success/:orderId?" element={<OrderSuccessPage />} />
        <Route path="/orders/callback" element={<OrderCallbackPage />} />
        <Route path="/request-refund/:orderId?" element={<RefundRequestPage />} />
        <Route path="/refund-status/:caseId?" element={<RefundStatusPage />} />
        
        {/* Catch-all for subdomains - redirect to store home */}
        <Route path="*" element={<StorePage username={subdomain} />} />
      </Routes>
    );
  }

  // Main app routes (web.paymeo.co)
  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={
          currentUser ? (
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          ) : (
            <LandingPage />
          )
        } />
        
        <Route path="/login" element={<LandingPage />} />
        <Route path="/signup" element={<LandingPage />} />
        <Route path="/invoice/v1/:id" element={<InvoicePreview />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/docs/api" element={<APIDocumentation />} />
        <Route path="/product/:productId" element={<SharedProductPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/track/:orderId?" element={<OrderTrackingPage />} />
        <Route path="/confirm-delivery/:orderId?" element={<OrderConfirmationPage />} />
        <Route path="/orders-success/:orderId?" element={<OrderSuccessPage />} />
        <Route path="/orders/callback" element={<OrderCallbackPage />} />
        <Route path="/request-refund/:orderId?" element={<RefundRequestPage />} />
        <Route path="/refund-status/:caseId?" element={<RefundStatusPage />} />
        <Route path="/auth/magic" element={<MagicLinkPage />} />
        <Route path="/black/lsg" element={<ScanProduct />} />

        {/* Store page with company ID param - PUBLICLY ACCESSIBLE */}
        <Route path="/s/:companyId" element={<StorePage />} />

        {/* Protected routes */}
        <Route path="/auth/catalog/start" element={
          <ProtectedRoute>
            <Layout>
          <CatalogInit/>
            </Layout>
          </ProtectedRoute>
          } />

        <Route path="/edit/:productId" element={
          <ProtectedRoute>
            <Layout>
              <EditProduct />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/setup/complete-profile" element={
          <ProtectedRoute>
            <CompleteSignupPage />
          </ProtectedRoute>
        } />

        <Route path="/setup/select-market" element={
          <ProtectedRoute>
            <SelectMarketPage />
          </ProtectedRoute>
        } />

        <Route path="/setup/upload-product" element={
          <ProtectedRoute>
            <UploadProductPage />
          </ProtectedRoute>
        } />

        <Route path="/new/invoice" element={
          <ProtectedRoute>
            <Layout>
              <InvoicePage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/customers" element={
          <ProtectedRoute>
            <Layout>
              <CustomersPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/meoai" element={
          <ProtectedRoute>
            <Layout>
              <MeoAIPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/wallet" element={
          <ProtectedRoute>
            <Layout>
              <Wallet />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/maps" element={
          <ProtectedRoute>
            <Layout>
              <MapsPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/store" element={
          <ProtectedRoute>
            <Layout>
              <ProductsPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/transactions" element={
          <ProtectedRoute>
            <Layout>
              <TransactionsPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/settings" element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/invoices" element={
          <ProtectedRoute>
            <Layout>
              <AllInvoicesPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/chats" element={
          <ProtectedRoute>
            <Layout>
              <Chats />
            </Layout>
          </ProtectedRoute>
        } />


        <Route path="/tempASK" element={
          <ProtectedRoute>
            <Layout>
             <AsksDataComponent 
    onAuthRequired={() => {console.log("Auth required callback triggered") }}
    showActions={true}
/>
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/orders" element={
          <ProtectedRoute>
            <Layout>
              <MainOrdersPage />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/customers/edit/:id" element={
          <ProtectedRoute>
            <Layout>
              <EditCustomer />
            </Layout>
          </ProtectedRoute>
        } />

        {/* Admin routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
        <Route path="/admin/dashboard" element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        } />

        {/* Catch-all route */}
        <Route path="*" element={
          currentUser ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <h2 className="text-2xl font-bold text-gray-900">404 - Page Not Found</h2>
              <p className="text-gray-600">The page you're looking for doesn't exist.</p>
              <a href="/dashboard" className="text-blue-600 hover:text-blue-800">
                Go to Dashboard
              </a>
            </div>
          ) : (
            <LandingPage />
          )
        } />
      </Routes>

      <InstallPrompt />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <MeoProvider>
            <AddressBookProvider>
              <BusinessSsoGate>
                <AppRoutes />
              </BusinessSsoGate>
            </AddressBookProvider>
          </MeoProvider>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;